import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Collapse from '@mui/material/Collapse';
import { Link, useTheme } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

const Navbar = ({ rightContent }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [notes, setNotes] = useState([]);
    const [notesExpanded, setNotesExpanded] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const apiUrl = process.env.REACT_APP_BACKEND_API;
    const open = Boolean(anchorEl);

    useEffect(() => {
        const token = localStorage.getItem('token');
        setIsAuthenticated(!!token);
        
        if (token) {
            fetchNotes();
        }
    }, [location.pathname]);

    const fetchNotes = async () => {
        try {
            const response = await fetch(`${apiUrl}/users/get_notes_list`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setNotes(data);
            }
        } catch (error) {
            console.error('Error fetching notes:', error);
        }
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setNotesExpanded(true);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setNotesExpanded(false);
    };

    const handleNotesToggle = () => {
        setNotesExpanded(!notesExpanded);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/signin');
        handleClose();
    };

    const handleNoteClick = (noteId) => {
        navigate(`/note/${noteId}`);
        handleClose();
    };

    return (
        <Box 
            sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
            }}
        >
            <Link 
                href="/" 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    textDecoration: 'none',
                    color: 'text.primary',
                }}
            >
                <Typography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                        fontWeight: theme.typography.h4.fontWeight,
                        color: 'text.primary',
                        fontSize: '26px',
                    }}
                >
                    tenderead
                </Typography>
            </Link>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                {rightContent}
                <IconButton 
                    edge="end" 
                    aria-label="menu"
                    onClick={handleClick}
                    sx={{ color: 'text.primary' }}
                >
                    <MoreVertIcon />
                </IconButton>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        minWidth: '200px',
                        borderRadius: '6px',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        border: '1px solid',
                        borderColor: 'divider',
                        marginTop: theme.spacing(0.5),
                        padding: theme.spacing(0.25, 0),
                    }
                }}
            >
                <MenuItem
                    onClick={() => {
                        navigate('/dashboard');
                        handleClose();
                    }}
                    sx={{
                        fontSize: '14px',
                        padding: theme.spacing(0.25, 1),
                        minHeight: 'auto',
                        '&:hover': {
                            backgroundColor: 'action.hover',
                        },
                    }}
                >
                    Dashboards
                </MenuItem>
                
                {isAuthenticated && (
                    <>
                        <MenuItem
                            onClick={handleNotesToggle}
                            sx={{
                                fontSize: '14px',
                                padding: theme.spacing(0.25, 1),
                                minHeight: 'auto',
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                },
                            }}
                        >
                            Notes
                            <Box sx={{ marginLeft: 'auto' }}>
                                {notesExpanded ? <ExpandLess /> : <ExpandMore />}
                            </Box>
                        </MenuItem>
                        
                        <Collapse in={notesExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ pl: theme.spacing(1.5), pr: theme.spacing(0.5), py: theme.spacing(0.25) }}>
                                {notes.length > 0 ? (
                                    notes.map((note) => (
                                        <MenuItem
                                            key={note.id}
                                            onClick={() => handleNoteClick(note.id)}
                                            sx={{
                                                fontSize: '14px',
                                                padding: theme.spacing(0.25, 0.75),
                                                minHeight: 'auto',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                },
                                            }}
                                        >
                                            {note.title || 'Untitled'}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '14px',
                                            color: 'text.secondary',
                                            padding: theme.spacing(0.25, 0.75),
                                            display: 'block',
                                        }}
                                    >
                                        No notes
                                    </Typography>
                                )}
                            </Box>
                        </Collapse>
                    </>
                )}

                {isAuthenticated && (
                    <MenuItem
                        onClick={handleLogout}
                        sx={{
                            fontSize: '14px',
                            padding: theme.spacing(0.25, 1),
                            minHeight: 'auto',
                            borderTop: `1px solid ${theme.palette.divider}`,
                            marginTop: theme.spacing(0.5),
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        logout
                    </MenuItem>
                )}
            </Menu>
        </Box>
    );
};

export default Navbar;

